import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  KeyRound,
  LayoutDashboard,
  Laptop,
  Search,
  Shield,
  ShieldAlert,
  User as UserIcon,
  UserCog,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface Destination {
  to: string;
  label: string;
  group: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
}

const destinations: Destination[] = [
  { to: '/', label: 'Dashboard', group: 'Workspace', icon: LayoutDashboard },
  { to: '/profile', label: 'Profile', group: 'Account', icon: UserIcon },
  { to: '/profile/password', label: 'Change password', group: 'Account', icon: KeyRound },
  { to: '/sessions', label: 'Session history', group: 'Account', icon: Activity },
  { to: '/devices', label: 'Active devices', group: 'Account', icon: Laptop },
  { to: '/roles', label: 'Role management', group: 'Administration', icon: UserCog, permission: 'roles.view' },
  { to: '/permissions', label: 'Permission management', group: 'Administration', icon: Shield, permission: 'roles.view' },
  { to: '/logs/activity', label: 'Activity logs', group: 'Administration', icon: Activity },
  { to: '/logs/security', label: 'Security logs', group: 'Administration', icon: ShieldAlert, permission: 'audit_logs.view' },
];

/**
 * Client-side "jump to page" command palette (⌘K / Ctrl+K). There's no
 * cross-entity search endpoint yet (projects/tasks/CRM records aren't
 * built in this pass), so this searches real navigation destinations
 * rather than faking record results — see webapp/README.md.
 */
export function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const results = useMemo(() => {
    const allowed = destinations.filter((d) => !d.permission || hasPermission(d.permission));
    const q = query.trim().toLowerCase();
    if (!q) {
      return allowed;
    }
    return allowed.filter((d) => d.label.toLowerCase().includes(q) || d.group.toLowerCase().includes(q));
  }, [query, hasPermission]);

  useEffect(() => {
    function handleGlobalKeydown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsOpen((v) => !v);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleGlobalKeydown);
    return () => document.removeEventListener('keydown', handleGlobalKeydown);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHighlighted(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  function go(destination: Destination) {
    navigate(destination.to);
    setIsOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && results[highlighted]) {
      event.preventDefault();
      go(results[highlighted]);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex w-full max-w-sm items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm text-gray-400 transition-colors hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search or jump to…</span>
        <kbd className="rounded-md border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:border-gray-700 dark:bg-gray-800">
          ⌘K
        </kbd>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-[12vh] backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="flex items-center gap-2.5 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search or jump to a page…"
                  className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100"
                />
                <kbd className="rounded-md border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-400 dark:border-gray-700">Esc</kbd>
              </div>
              <div className="max-h-80 overflow-y-auto p-2">
                {results.length === 0 && (
                  <p className="px-3 py-6 text-center text-sm text-gray-400">No matching pages.</p>
                )}
                {results.map((destination, index) => (
                  <button
                    key={destination.to}
                    type="button"
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => go(destination)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      index === highlighted
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <destination.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{destination.label}</span>
                    <span className="text-xs text-gray-400">{destination.group}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
